/**
 * The entry window's Organizations tab — which organizations get this
 * connector (item 65A, operator brief 2026-10-30: easy, default all, a real
 * sense of control).
 *
 * On the owner's own page: "All organizations under <owner>" (organizations
 * added later too) or "Only these organizations", picked on the tree —
 * ticking an organization ticks everything under it, a partial box says some
 * of a branch is chosen, search keeps each match in its place in the tree,
 * Select all / Clear, "3 of 5" per branch, and a live "Available to 7 of 10
 * organizations". Every organization shows its REAL state beside the choice:
 * removed by its own manager (with "Give it back"), removed by a level above,
 * defaults changed there, the agents there that use it. Saving a narrowing
 * first names the agents that lose it, by organization.
 *
 * On a lower level's page the owner's choice is read-only; each organization
 * of this part of the tree has an on / off switch — a lower level can only
 * narrow (the server's one rule decides what a switch does and says why one
 * cannot move).
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Checkbox, Switch } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { showNotification } from '@/utils/showNotification';
import {
  connectorLibraryService,
  errorBody,
  type Availability,
  type AvailabilityMode,
  type AvailabilityOrg,
  type ChangeOutcome,
  type ChangePreview,
} from '../services/connectorLibraryService';
import {
  branchCount, buildTree, check, flatten, removedBy, sameChoice, search, shownNodes, toggle, type TreeNode,
} from '../utils/orgTree';
import { PrimaryAction } from '@modules/chatbots/components/ConnectorDialog';
import { outcomeText } from './LibraryPanels';
import { GivingMeans } from './SharingTexts';
import './LibraryOrganizations.css';

type T = (key: string, options?: Record<string, unknown>) => string;
type Node = TreeNode<AvailabilityOrg>;

interface Props {
  entryId: string;
  tenantId: string;
  /** Something changed: the list reloads its counts. */
  onChanged: (outcome: ChangeOutcome | null) => void;
  /** An unsaved choice: closing the window asks first. */
  onDirty: (dirty: boolean) => void;
}

export function OrganizationsPanel({ entryId, tenantId, onChanged, onDirty }: Props) {
  const { t } = useLanguage();
  const questionId = useId();
  const [av, setAv] = useState<Availability | null>(null);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState<AvailabilityMode>('all');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<{ preview: ChangePreview; run: () => Promise<void> } | null>(null);
  const [busy, setBusy] = useState(false);

  const start = useCallback((next: Availability) => {
    setAv(next);
    setMode(next.mode);
    setChosen(new Set(next.mode === 'only' ? next.tenant_ids : next.organizations.map(o => o.tenant_id)));
    // The organization it opens on and the level under it are open.
    setExpanded(new Set(next.organizations.filter(o => o.parent_id === null).map(o => o.tenant_id)));
    setConfirm(null);
  }, []);

  // Only the latest read lands: an earlier one answering late must never
  // reset a choice the person has started making.
  const latest = useRef(0);
  const load = useCallback(async () => {
    const mine = ++latest.current;
    setFailed(false);
    try {
      const next = await connectorLibraryService.availability(entryId, tenantId);
      if (mine === latest.current) start(next);
    } catch {
      if (mine === latest.current) setFailed(true);
    }
  }, [entryId, tenantId, start]);

  useEffect(() => { setAv(null); void load(); }, [load]);

  const tree = useMemo(() => buildTree(av?.organizations ?? []), [av]);
  const rows = useMemo(() => flatten(tree), [tree]);
  const found = useMemo(() => search(tree, query), [tree, query]);
  const before = useMemo(() => new Set(av ? (av.mode === 'only' ? av.tenant_ids : av.organizations.map(o => o.tenant_id)) : []), [av]);
  const dirty = !!av && (mode !== av.mode || (mode === 'only' && !sameChoice(chosen, before)));
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);
  useEffect(() => () => onDirty(false), [onDirty]);

  if (failed) {
    return (
      <div className="cxl-panel__error" role="alert">
        <span>{t('connectorLibrary.orgs.loadError')}</span>{' '}
        <button type="button" className="cx-btn cx-btn--sm" onClick={() => void load()}>{t('common.retry')}</button>
      </div>
    );
  }
  if (!av) return <div className="cxl-loading" aria-busy="true"><span className="loading-spinner" /></div>;

  // The live count: chosen, less the organizations a manager removed it from.
  const removed = new Set(av.organizations.filter(o => o.state === 'hidden' || o.state === 'hidden_above').map(o => o.tenant_id));
  const picked = mode === 'all' ? new Set(av.organizations.map(o => o.tenant_id)) : chosen;
  const available = [...picked].filter(id => !removed.has(id)).length;
  const removedChosen = [...picked].filter(id => removed.has(id)).length;
  const owner = av.owner_name ?? '';

  /** Ask the server what it would do; say so when agents lose it, else just do it. */
  const guarded = async (preview: () => Promise<ChangePreview | null>, run: () => Promise<void>) => {
    setBusy(true);
    try {
      const p = await preview();
      if (p && p.detaches > 0) setConfirm({ preview: p, run });
      else await run();
    } catch (err) {
      showNotification(errorBody(err).error || t('connectorLibrary.orgs.saveError'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveChoice = () => guarded(
    () => connectorLibraryService.preview(entryId, tenantId, 'availability', { mode, tenant_ids: [...chosen] }),
    async () => {
      const { outcome, availability } = await connectorLibraryService.setAvailability(
        entryId, tenantId, mode, [...chosen], av.version);
      start(availability);
      onChanged(outcome);
      showNotification(`${t('connectorLibrary.orgs.saved')} ${outcomeText(t, outcome)}.`, 'success');
    });

  const flip = (org: AvailabilityOrg) => {
    const on = org.state !== 'on';
    return guarded(
      () => connectorLibraryService.previewSwitch(org.tenant_id, entryId, on),
      async () => {
        const { outcome } = await connectorLibraryService.switchHere(org.tenant_id, entryId, on);
        await load();
        onChanged(outcome);
        showNotification(`${t(on ? 'connectorLibrary.orgs.turnedOn' : 'connectorLibrary.orgs.turnedOff',
          { organization: org.name })} ${outcomeText(t, outcome)}.`, 'success');
      });
  };

  const run = async () => {
    const c = confirm;
    if (!c) return;
    setBusy(true);
    try {
      await c.run();
    } catch (err) {
      showNotification(errorBody(err).error || t('connectorLibrary.orgs.saveError'), 'error');
      await load();
    } finally {
      setConfirm(null);
      setBusy(false);
    }
  };

  const choosing = av.can_choose && mode === 'only';
  // A row shows when every organization above it is open (or a search opened it).
  const shown = shownNodes(rows, found, id => expanded.has(id));

  return (
    <div className="cxl-orgs" data-testid="library-organizations">
      {/* Item 85: the question and its two answers at the end side, the tree they decide at the start
          side (stacked on a phone, the question first). First in reading order, as it is to be answered. */}
      <div className="cxl-orgs__layout">
        <section className="cxl-orgs__choose" aria-labelledby={questionId} data-testid="library-orgs-choose">
          <h4 id={questionId} className="cxl-orgs__question">{t('connectorLibrary.orgs.question')}</h4>
          {av.can_choose ? (
            <div className="cxl-orgs__modes cxl-orgs__modes--stacked" role="radiogroup" aria-labelledby={questionId}>
              {(['all', 'only'] as const).map(m => (
                <label key={m} className={`cxl-orgs__mode ${mode === m ? 'is-on' : ''}`} data-testid={`library-orgs-mode-${m}`}>
                  <input type="radio" name={`orgs-mode-${entryId}`} checked={mode === m} disabled={busy}
                         onChange={() => {
                           setMode(m);
                           // "Only the organizations you tick" starts from who gets it now, so narrowing is unticking.
                           if (m === 'only' && av.mode === 'all') setChosen(new Set(av.organizations.map(o => o.tenant_id)));
                         }} />
                  <span className="cxl-orgs__mode-text">
                    <span className="cxl-orgs__mode-title">
                      {m === 'all' ? <>{t('connectorLibrary.orgs.all')} <bdi dir="auto">{owner}</bdi></> : t('connectorLibrary.orgs.only')}
                    </span>
                    <span className="cxl-orgs__mode-hint">{t(m === 'all' ? 'connectorLibrary.orgs.allHint' : 'connectorLibrary.orgs.onlyHint')}</span>
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <p className="cxl-panel__lead" data-testid="library-orgs-owner-choice">
              {av.mode === 'all' ? t('connectorLibrary.orgs.ownerChoseAll', { owner }) : t('connectorLibrary.orgs.ownerChoseSome', { owner })}
              {' '}{t('connectorLibrary.orgs.narrowOnly')}
            </p>
          )}
        </section>

        <div className="cxl-orgs__pick" data-testid="library-orgs-pick">
          <p className="cxl-orgs__summary" aria-live="polite" data-testid="library-orgs-summary">
            <strong>{t('connectorLibrary.orgs.summary', { count: available, total: av.total })}</strong>
            {removedChosen > 0 && <span> · {t('connectorLibrary.orgs.summaryRemoved', { count: removedChosen })}</span>}
          </p>

          {av.total > 1 && (
            <div className="cxl-orgs__tools">
              <input className="form-input cxl-orgs__search" type="search" value={query} dir="auto"
                     onChange={e => setQuery(e.target.value)} placeholder={t('connectorLibrary.orgs.search')}
                     aria-label={t('connectorLibrary.orgs.search')} data-testid="library-orgs-search" />
              {choosing && (
                <>
                  <button type="button" className="cx-btn cx-btn--sm" disabled={busy}
                          onClick={() => setChosen(new Set(av.organizations.map(o => o.tenant_id)))}
                          data-testid="library-orgs-select-all">{t('connectorLibrary.orgs.selectAll')}</button>
                  <button type="button" className="cx-btn cx-btn--sm" disabled={busy} onClick={() => setChosen(new Set())}
                          data-testid="library-orgs-clear">{t('connectorLibrary.orgs.clear')}</button>
                </>
              )}
            </div>
          )}

          {query.trim() && found.visible.size === 0 ? (
            <p className="cxl-panel__empty">{t('connectorLibrary.orgs.noMatch')}</p>
          ) : (
            <ul className="cxl-orgs__tree" role="tree" aria-label={t('connectorLibrary.orgs.treeLabel')} aria-multiselectable={choosing}>
              {shown.map(node => (
                <OrgRow key={node.org.tenant_id} node={node} t={t} choosing={choosing} canChoose={av.can_choose}
                        chosen={chosen} busy={busy} match={found.matches.has(node.org.tenant_id)}
                        open={expanded.has(node.org.tenant_id) || found.open.has(node.org.tenant_id)}
                        onOpen={() => setExpanded(prev => {
                          const next = new Set(prev);
                          if (next.has(node.org.tenant_id)) next.delete(node.org.tenant_id); else next.add(node.org.tenant_id);
                          return next;
                        })}
                        onTick={() => setChosen(prev => toggle(node, prev))}
                        onFlip={() => { void flip(node.org); }} />
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* What giving means: at the end of the window, above Save (item 80b). */}
      {av.can_choose && <GivingMeans />}

      {confirm && (
        <LosesIt t={t} preview={confirm.preview} busy={busy} onBack={() => setConfirm(null)} onGo={() => { void run(); }} />
      )}

      {av.can_choose && !confirm && (
        <div className="cxl-orgs__footer">
          {mode === 'only' && chosen.size === 0 && (
            <span className="cxl-orgs__footer-note" role="status">{t('connectorLibrary.orgs.pickOne')}</span>
          )}
          {dirty && mode === 'only' && chosen.size > 0 && removedBy(before, chosen).length > 0 && (
            <span className="cxl-orgs__footer-note">
              {t('connectorLibrary.orgs.removing', { count: removedBy(before, chosen).length })}
            </span>
          )}
          <button type="button" className="btn-cancel" disabled={!dirty || busy} onClick={() => start(av)}
                  data-testid="library-orgs-undo">{t('connectorLibrary.orgs.undo')}</button>
          <PrimaryAction label={t('connectorLibrary.orgs.save')} busy={busy}
                         disabled={!dirty || (mode === 'only' && chosen.size === 0)}
                         onClick={() => { void saveChoice(); }} testId="library-orgs-save" />
        </div>
      )}
    </div>
  );
}

function OrgRow({ node, t, choosing, canChoose, chosen, busy, match, open, onOpen, onTick, onFlip }: {
  node: Node; t: T; choosing: boolean; canChoose: boolean; chosen: Set<string>; busy: boolean; match: boolean;
  open: boolean; onOpen: () => void; onTick: () => void; onFlip: () => void;
}) {
  const org = node.org;
  const state = check(node, chosen);
  const count = branchCount(node, chosen);
  const hasKids = node.children.length > 0;
  const refusal = org.switch.reason ? t(`connectorLibrary.orgs.refusal.${org.switch.reason}`, { name: org.hidden_at_name ?? '' }) : '';
  return (
    <li className={`cxl-orgs__row ${match ? 'is-match' : ''}`} role="treeitem" aria-level={node.depth + 1}
        aria-expanded={hasKids ? open : undefined} aria-selected={choosing ? state === 'checked' : undefined}
        style={{ '--depth': node.depth } as CSSProperties} data-testid={`library-org-${org.tenant_id}`}>
      <span className="cxl-orgs__caret-slot">
        {hasKids && (
          <button type="button" className={`cxl-orgs__caret ${open ? 'is-open' : ''}`} onClick={onOpen}
                  aria-label={t(open ? 'connectorLibrary.orgs.collapse' : 'connectorLibrary.orgs.expand', { name: org.name })}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        )}
      </span>
      {choosing ? (
        <Checkbox checked={state === 'checked'} indeterminate={state === 'partial'} disabled={busy} onChange={onTick}
                  className="cxl-orgs__check" data-testid={`library-org-check-${org.tenant_id}`}>
          <bdi dir="auto" className="cxl-orgs__name">{org.name}</bdi>
        </Checkbox>
      ) : (
        <span className="cxl-orgs__label">
          <span className={`cxl-orgs__dot cxl-orgs__dot--${org.state}`} aria-hidden="true" />
          <bdi dir="auto" className="cxl-orgs__name">{org.name}</bdi>
        </span>
      )}
      <span className="cxl-orgs__facts">
        {choosing && hasKids && (
          <span className="cxl-orgs__branch" data-testid={`library-org-branch-${org.tenant_id}`}>
            {t('connectorLibrary.orgs.branch', { count: count.chosen, total: count.total })}
          </span>
        )}
        <StateBadge t={t} org={org} choosing={choosing} />
        {org.changed_here && <span className="cx-pill cxl-pill--edited">{t('connectorLibrary.orgs.changedHere')}</span>}
        {org.agents > 0 && <span className="cxl-orgs__agents">{t('connectorLibrary.orgs.agents', { count: org.agents })}</span>}
        {org.people > 0 && <span className="cxl-orgs__agents">{t('connectorLibrary.orgs.people', { count: org.people })}</span>}
      </span>
      <span className="cxl-orgs__end">
        {canChoose ? (
          org.switch.action === 'unhide' && (
            <button type="button" className="cxl-row__link" disabled={busy} onClick={onFlip}
                    data-testid={`library-org-giveback-${org.tenant_id}`}>{t('connectorLibrary.orgs.giveBack')}</button>
          )
        ) : (
          <Switch size="small" checked={org.state === 'on'} disabled={busy || !org.switch.action} onChange={onFlip}
                  title={refusal || undefined}
                  aria-label={t('connectorLibrary.orgs.switchLabel', { name: org.name })}
                  data-testid={`library-org-switch-${org.tenant_id}`} />
        )}
      </span>
    </li>
  );
}

function StateBadge({ t, org, choosing }: { t: T; org: AvailabilityOrg; choosing: boolean }) {
  if (org.state === 'hidden') {
    return <span className="cx-pill cxl-pill--muted" data-state="hidden">{t('connectorLibrary.orgs.state.hidden')}</span>;
  }
  if (org.state === 'hidden_above') {
    return (
      <span className="cx-pill cxl-pill--muted" data-state="hidden_above">
        {t('connectorLibrary.orgs.state.hiddenAbove')} <bdi dir="auto">{org.hidden_at_name}</bdi>
      </span>
    );
  }
  // Under "Only these" the box already says it is not chosen.
  if (org.state === 'not_offered' && !choosing) {
    return <span className="cx-pill cxl-pill--muted" data-state="not_offered">{t('connectorLibrary.orgs.state.notOffered')}</span>;
  }
  return null;
}

/** Before a narrowing: who loses it, by organization — the server's own count. */
function LosesIt({ t, preview, busy, onBack, onGo }: {
  t: T; preview: ChangePreview; busy: boolean; onBack: () => void; onGo: () => void;
}) {
  const byOrg = new Map<string, ChangePreview['detached']>();
  preview.detached.forEach(a => byOrg.set(a.tenant_name ?? '', [...(byOrg.get(a.tenant_name ?? '') ?? []), a]));
  const keys = preview.detached.filter(a => a.needs_key).length;
  // Under a long tree the question would open below the fold: bring it into view.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.scrollIntoView({ block: 'nearest' }); }, []);
  return (
    <div className="cxl-orgs__loses" role="alert" data-testid="library-orgs-loses" ref={ref}>
      <p className="cxl-orgs__loses-title">
        {t('connectorLibrary.orgs.losesTitle', { agents: t('connectorLibrary.orgs.agentsCount', { count: preview.detaches }),
                                                 organizations: t('connectorLibrary.orgs.orgsCount', { count: preview.detached_organizations }) })}
      </p>
      <p className="cxl-orgs__loses-hint">
        {t('connectorLibrary.orgs.losesHint')}
        {keys > 0 && <> {t('connectorLibrary.orgs.losesKeys', { count: keys })}</>}
      </p>
      <ul className="cxl-orgs__loses-list">
        {[...byOrg.entries()].map(([org, agents]) => (
          <li key={org}>
            <bdi dir="auto" className="cxl-orgs__loses-org">{org}</bdi>
            {': '}
            {agents.map((a, i) => (
              <span key={a.chatbot_id}>
                {i > 0 && ', '}
                <bdi dir="auto">{a.chatbot_name}</bdi>
                {a.needs_key && <span className="cxl-orgs__loses-key"> ({t('connectorLibrary.orgs.needsKey')})</span>}
              </span>
            ))}
          </li>
        ))}
      </ul>
      <div className="cxl-orgs__footer">
        <button type="button" className="btn-cancel" disabled={busy} onClick={onBack} data-testid="library-orgs-back">
          {t('connectorLibrary.orgs.back')}
        </button>
        <button type="button" className="cx-btn cx-btn--danger cx-btn--lg" disabled={busy} onClick={onGo} data-testid="library-orgs-confirm">
          {t('connectorLibrary.orgs.confirm')}
        </button>
      </div>
    </div>
  );
}

